import { afterNextRender, Component, DestroyRef, inject, Injector, signal, viewChild, WritableSignal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { I18NextPipe } from 'angular-i18next';
import { isNil } from 'lodash';
import { ButtonComponent, ModalComponent, ModalFooterDirective } from 'mbs-ui-kit';
import { CompanyDestination } from 'mbs-ui-shared@models/Company';
import { SAWithDestinations } from 'mbs-ui-shared@models/StorageAccountsWithDestinations';
import { ScheduleStepValue } from 'mbs-ui-shared@models/backup/schedule-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { NBFScheduleStepComponent } from 'wizards/steps/schedule-step/NBF/schedule-step.component';
import { ScheduleStepComponent } from 'wizards/steps/schedule-step/schedule-step.component';
import { CompanyBackupTemplate } from '../../models/company-wizard-models';
import {
  applyTemplateStorageContext,
  borrowPlanContext,
  companyDestinationsToStorages,
  planContextFromTemplate,
  scheduleContextFromTemplate,
  templateAdvancedOptionsFlags,
  templateAdvancedStorage,
  templateStorageConnection
} from '../../utils/company-wizard-plan-context.util';
import { TemplateRetentionAlerts } from '../../utils/company-wizard-retention-alerts.util';

export type ScheduleOptionsModalData = {
  companyDestinations: CompanyDestination[];
  availableStorages: SAWithDestinations[];
  template: CompanyBackupTemplate;
};

export type ScheduleOptionsModalResult = {
  schedule: string;
  scheduleStep: ScheduleStepValue;
};

@Component({
  imports: [
    ButtonComponent,
    FormsModule,
    I18NextPipe,
    ModalComponent,
    ModalFooterDirective,
    NBFScheduleStepComponent,
    ScheduleStepComponent
  ],
  selector: 'mbs-schedule-options-modal',
  standalone: true,
  styleUrls: ['./schedule-options-modal.component.scss'],
  templateUrl: './schedule-options-modal.component.html'
})
export class ScheduleOptionsModalComponent {
  private readonly destroyRef = inject(DestroyRef);
  private readonly i18nPipe = inject(I18NextPipe);
  private readonly injector = inject(Injector);
  private readonly mainService = inject(RemoteManagementWizardsService);
  private readonly stepService = inject(WizardStepsService);
  private readonly retentionAlerts = new TemplateRetentionAlerts();

  protected readonly baseModal = viewChild(ModalComponent);
  protected readonly stepComponent = viewChild(NBFScheduleStepComponent);
  protected readonly legacyStepComponent = viewChild(ScheduleStepComponent);

  public readonly isUnix: WritableSignal<boolean> = signal(false);
  public readonly ready: WritableSignal<boolean> = signal(false);
  public readonly value: WritableSignal<ScheduleStepValue> = signal(null);

  public readonly title = this.i18nPipe.transform('wizards:schedule_title');

  constructor() {
    afterNextRender(() => this.applyModalData());
  }

  public saveClickHandler(): void {
    this.stepComponent()?.forceValid({ isSave: true });
    this.legacyStepComponent()?.forceValid({ isSave: true });

    if (this.value()?.valid === false) return;

    this.baseModal().save({ schedule: this.scheduleText(), scheduleStep: this.value() } as ScheduleOptionsModalResult);
  }

  public cancelClickHandler(): void {
    this.baseModal().close();
  }

  // Private section
  private applyModalData(): void {
    const data = this.baseModal()?.data as ScheduleOptionsModalData;

    if (!data) return;

    const { template } = data;
    // Unix agents only have the legacy schedule step, which offers no FFI; in RD mode it hides the overdue settings,
    // as the RD configuration wizard does with this very step
    const context = template.isUnix ? planContextFromTemplate(template) : scheduleContextFromTemplate(template);

    this.destroyRef.onDestroy(borrowPlanContext(this.mainService, context));
    this.retentionAlerts.apply(template.destinationPlusRetention);

    this.isUnix.set(template.isUnix);
    this.value.set(template.scheduleStep);
    this.ready.set(true);

    // pipeProperty does not replay, and the hosted step subscribes as it is created, so the calculated properties
    // have to be published once the step exists
    afterNextRender(() => this.prepareStepProperties(data), { injector: this.injector });
  }

  private prepareStepProperties(data: ScheduleOptionsModalData): void {
    const { availableStorages, companyDestinations, template } = data;
    const advancedOptions = template.advancedOptions;
    const retention = template.destinationPlusRetention?.retentionPolicyStep;

    const connection = templateStorageConnection(template, companyDestinations, availableStorages);
    const agentVersion = this.mainService.backupVersionUpdated;

    applyTemplateStorageContext(this.stepService, {
      advancedStorage: templateAdvancedStorage(template, templateAdvancedOptionsFlags(template, connection, agentVersion)),
      agentVersion,
      connection,
      storages: companyDestinationsToStorages(companyDestinations, availableStorages)
    });

    // Publishing a missing flag as false would read as "synthetic full is off" and lock the FFI tile, and the option
    // does not exist at all for templates whose advanced options never carry it
    if (!isNil(advancedOptions?.SyntheticFull)) {
      this.stepService.updateProperty('options', 'syntheticFull', advancedOptions.SyntheticFull);
    }

    this.stepService.updateProperty('retention', 'isGFSMode', !!retention?.GFSPolicySettings?.enabled);
  }

  private scheduleText(): string {
    const parts = this.isUnix() ? this.legacyScheduleParts() : this.scheduleParts();

    return parts.length ? parts.join('; ') : this.i18nPipe.transform('wizards:schedule.defaultMode.noSchedule');
  }

  private scheduleParts(): string[] {
    const info = this.stepService.getSnapshot('schedule', 'scheduleInfo');

    return [info?.simple, info?.incremental, info?.full].filter(Boolean);
  }

  private legacyScheduleParts(): string[] {
    const { fullBackupData, ScheduleType } = this.value();
    const texts = this.legacyStepComponent().schedulesTexts;

    if (ScheduleType !== 'predefined') return [];

    return [
      `${this.i18nPipe.transform('wizards:incremental_text')} ${texts.incrementalData}`,
      fullBackupData?.enabled && `${this.i18nPipe.transform('wizards:full_text')} ${texts.fullBackupData}`
    ].filter(Boolean);
  }
}
